import { Link, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@/lovable-demo/hooks/use-server-fn";
import { createContext, useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Activity,
  BarChart3,
  CircleDot,
  Factory,
  FileText,
  Gauge,
  Layers,
  ListOrdered,
  Users,
  Radar,
  Receipt,
  RotateCcw,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  Sigma,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import { Button } from "@/components/ui/button";
import { AccountBar, SignOutButton } from "@/lovable-demo/components/account-bar";
import { usePermissions } from "@/lovable-demo/hooks/use-permissions";
import { resetSampleData } from "@/lovable-demo/lib/governance/users.functions";
import { SAMPLE_DATA_NOTE } from "@/lovable-demo/lib/warranty/export";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV = [
  { to: "/demo/dashboard", label: "Executive Dashboard", icon: Gauge },
  { to: "/demo/open-problem-records", label: "Open Problem Records", icon: ListOrdered },
  { to: "/demo/portfolio", label: "All Programs", icon: Layers },
  { to: "/demo/signals", label: "Signal Intelligence", icon: Radar },
  { to: "/demo/problem-records", label: "Problem Records", icon: ListOrdered },
  { to: "/demo/claims", label: "Warranty Claims", icon: Receipt },
  { to: "/demo/safety", label: "Safety & NHTSA", icon: ShieldAlert },
  { to: "/demo/compliance", label: "IATF & ISO 9001", icon: ScrollText },
  { to: "/demo/closed-loop", label: "Closed Loop", icon: CircleDot },
  { to: "/demo/top5", label: "Top 5 Intelligence", icon: Activity },
  { to: "/demo/prevention", label: "Prevention", icon: ShieldCheck },
  { to: "/demo/suppliers", label: "Suppliers", icon: Factory },
  { to: "/demo/financial", label: "Financial", icon: Wallet },
  { to: "/demo/executive", label: "Executive Summary", icon: BarChart3 },
  { to: "/demo/brief", label: "Briefing", icon: FileText },
] as const;

const DemoShellContext = createContext(false);

type AppShellProps = {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
};

export function AppShell(props: AppShellProps) {
  const nested = useContext(DemoShellContext);
  if (nested)
    return (
      <section className="space-y-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">{props.title}</h1>
            {props.subtitle && (
              <p className="mt-1 text-sm text-muted-foreground">{props.subtitle}</p>
            )}
          </div>
          {props.actions}
        </header>
        {props.children}
      </section>
    );
  return (
    <DemoShellContext.Provider value={true}>
      <ShellFrame {...props} />
    </DemoShellContext.Provider>
  );
}

function ShellFrame({ title, subtitle, actions, children }: AppShellProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data, activeProgramId, setActiveProgram } = useWarranty();
  const { roles } = usePermissions();
  const canManage = roles.includes("admin") || roles.includes("quality_director");
  const reset = useServerFn(resetSampleData);
  const queryClient = useQueryClient();
  const [resetting, setResetting] = useState(false);
  const program = data.programs.find((p) => p.id === activeProgramId) ?? data.programs[0];

  return (
    <div data-lovable-demo-shell className="flex min-h-screen bg-background">
      <aside className="no-print sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar lg:flex">
        <Link
          to="/demo/programs"
          className="flex items-center gap-2.5 border-b border-sidebar-border px-4 py-4"
        >
          <span className="grid size-8 place-items-center rounded bg-primary/15 text-primary">
            <Sigma className="size-4" />
          </span>
          <span>
            <span className="block font-display text-sm font-semibold leading-tight">
              Broadview
            </span>
            <span className="block text-[0.66rem] uppercase tracking-[0.18em] text-muted-foreground">
              Intelligence
            </span>
          </span>
        </Link>

        <nav
          aria-label="Synthetic workflow navigation"
          className="flex-1 space-y-0.5 overflow-y-auto p-2"
        >
          {NAV.map((item) => {
            const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex items-center gap-2.5 rounded px-2.5 py-2 text-sm transition-colors",
                  active
                    ? "bg-sidebar-accent text-sidebar-primary"
                    : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
                )}
              >
                <item.icon className="size-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
          {canManage && (
            <Link
              to="/demo/users"
              className={cn(
                "flex items-center gap-2.5 rounded px-2.5 py-2 text-sm transition-colors",
                pathname === "/demo/users"
                  ? "bg-sidebar-accent text-sidebar-primary"
                  : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
              )}
            >
              <Users className="size-4 shrink-0" />
              Users & roles
            </Link>
          )}
        </nav>

        <div className="border-t border-sidebar-border p-3">
          <p className="text-[0.62rem] uppercase tracking-[0.16em] text-muted-foreground">
            Program
          </p>
          <select
            value={program?.id ?? ""}
            onChange={(e) => setActiveProgram(e.target.value)}
            className="mt-1.5 w-full rounded border border-input bg-elevated px-2 py-1.5 text-xs text-foreground"
          >
            {data.programs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
              </option>
            ))}
          </select>
          {canManage && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full justify-start text-xs text-muted-foreground"
              disabled={resetting}
              onClick={async () => {
                const rationale = window.prompt(
                  "Reason for resetting sample records (at least 10 characters):",
                );
                if (rationale === null) return;
                if (rationale.trim().length < 10) {
                  toast.error("Enter a rationale of at least 10 characters.");
                  return;
                }
                if (
                  !window.confirm(
                    "Restore this synthetic demo session to its exported baseline? Demo changes will be discarded.",
                  )
                )
                  return;
                setResetting(true);
                try {
                  await reset({ data: { rationale: rationale.trim() } });
                  await queryClient.invalidateQueries();
                  toast.success("Synthetic demo session restored.");
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : "Reset was not recorded.");
                } finally {
                  setResetting(false);
                }
              }}
            >
              <RotateCcw className="size-3.5" /> Reset sample data
            </Button>
          )}
          <AccountBar />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-20 border-b border-border bg-background/85 px-5 py-4 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="mb-1 text-[0.65rem] font-semibold uppercase tracking-wider text-primary">
                Synthetic demonstration · session changes only
              </p>
              <h1 className="truncate text-lg font-semibold">
                {title ?? "Synthetic workflow demo"}
              </h1>
              {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2">
              {actions}
              <ThemeToggle />
              <SignOutButton />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground lg:hidden">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded border border-border px-2 py-1 hover:text-foreground"
              >
                {item.label}
              </Link>
            ))}
            {canManage && (
              <Link
                to="/demo/users"
                className="rounded border border-border px-2 py-1 hover:text-foreground"
              >
                Users & roles
              </Link>
            )}
          </div>
          <div className="mt-3 lg:hidden">
            <AccountBar />
          </div>
        </header>
        <main className="print-full mx-auto w-full min-w-0 max-w-[1500px] flex-1 p-5">
          {children}
        </main>
        <footer className="border-t border-border px-5 py-3 text-[0.7rem] text-muted-foreground">
          Synthetic demonstration · changes stay in this demo session. {SAMPLE_DATA_NOTE}
        </footer>
      </div>
    </div>
  );
}
